
import React from 'react';
import { 
  Send, Download, Printer, ArrowLeft, Check
} from './Icons';
import { BrandLogo } from './BrandLogo';

interface DocumentPreviewProps {
  document: any;
  type: 'INVOICE' | 'ORDER';
  onBack: () => void;
}

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({ document: doc, type, onBack }) => {
  if (!doc) return null;

  const docNumber = doc.number || doc.orderNumber;
  const docDate = doc.date || doc.orderDate;
  
  const parseAmount = (val: any) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
       const clean = val.replace(/[^0-9.-]/g, '');
       return parseFloat(clean) || 0;
    }
    return 0;
  };

  const amountNet = parseAmount(doc.net);
  const amountGross = parseAmount(doc.gross);

  const formatSwiss = (val: number) => {
    return new Intl.NumberFormat('de-CH', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(val);
  };

  // If the document has a structured items list, we use it. Otherwise, fallback to a single default row.
  const hasItems = doc.items && Array.isArray(doc.items) && doc.items.length > 0;

  return (
    <div className="flex flex-col h-full w-full bg-[#f4f4f4] dark:bg-black animate-in fade-in duration-300">
      
      {/* Document Action Header - Black Bar */}
      <div className="h-14 border-b border-[#393939] flex items-center justify-between px-0 shrink-0 bg-[#161616] text-white shadow-md z-30">
        <div className="flex items-center h-full">
          <button 
            onClick={onBack}
            className="h-full px-6 flex items-center gap-3 hover:bg-[#262626] border-r border-[#393939] transition-colors group"
          >
            <ArrowLeft size={16} className="text-gray-400 group-hover:text-white transition-colors" /> 
            <span className="text-xs font-bold uppercase tracking-widest text-white">Back to List</span>
          </button>
          
          <div className="px-6 flex items-center gap-4">
            <h2 className="text-xl font-bold text-white tracking-tight">{docNumber}</h2>
            <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-[2px] ${
              (doc.status === 'completed' || doc.status === 'DONE' || doc.status === 'Sent' || doc.status === 'ORDERED') 
                ? 'bg-[#198038] text-white' 
                : (doc.status === 'failed' || doc.status === 'ERROR' || doc.status === 'Failed' || doc.status === 'FAILED') 
                  ? 'bg-[#DA1E28] text-white' 
                  : 'bg-[#F1C21B] text-[#161616]' 
            }`}>
              {doc.status || 'Draft'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center h-full">
           <button className="h-full px-6 flex items-center gap-2 hover:bg-[#262626] border-l border-[#393939] text-xs font-bold uppercase tracking-wider transition-colors text-white">
              <Download size={16} /> Download
           </button>
           <button className="h-full px-6 flex items-center gap-2 hover:bg-[#262626] border-l border-[#393939] text-xs font-bold uppercase tracking-wider transition-colors text-white">
              <Printer size={16} /> Print
           </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Document Area */}
        <div className="flex-1 overflow-y-auto p-8 lg:p-12 custom-scrollbar flex justify-center bg-[#F2F4F8] dark:bg-[#000000]">
           {/* Document Paper */}
           <div className="w-[850px] bg-white dark:bg-[#161616] shadow-sm p-12 lg:p-16 min-h-[1100px] border border-gray-200 dark:border-[#333] relative">
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.02] rotate-[-35deg]">
                <BrandLogo type="main" size={500} />
             </div>

             <div className="flex justify-between items-start mb-16 relative z-10">
                <div className="space-y-6">
                   <BrandLogo type="main" size={48} />
                   <div className="text-xs text-[#161616] dark:text-gray-300 leading-relaxed font-medium">
                      <p className="text-base font-bold tracking-tight mb-1 text-[#161616] dark:text-white">{doc.vendor || 'VMI Services AG'}</p>
                      <p>Corporate Fulfillment Center</p>
                      <p>9470 Buchs SG, Switzerland</p>
                      <p>UID: CHE-112.334.556</p>
                   </div>
                </div>
                <div className="text-right">
                   <h3 className="text-4xl font-light text-gray-300 dark:text-gray-700 uppercase mb-4 tracking-tighter">{type}</h3>
                   <div className="space-y-1">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Date: <span className="text-[#161616] dark:text-white">{docDate}</span></p>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Number: <span className="text-[#161616] dark:text-white">{docNumber}</span></p>
                   </div>
                </div>
             </div>

             <div className="mb-16 grid grid-cols-2 gap-12 relative z-10">
                <div className="space-y-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#717C26]">Recipient</p>
                  <div className="text-sm font-bold text-[#161616] dark:text-white leading-tight">
                    <p className="text-lg mb-1">{doc.customer}</p>
                    <p className="font-medium text-gray-500">Inventory Management Dept.</p>
                    <p className="font-medium text-gray-500">8001 Zürich, CH</p>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-[#202020] p-6 border border-gray-100 dark:border-[#333] self-start">
                   <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Terms</p>
                   <p className="text-sm font-medium text-[#161616] dark:text-white">Due Date: {doc.dueBy || 'Immediate'}</p>
                   <p className="text-sm font-medium text-[#161616] dark:text-white mt-1">Automatic VMI Settlement</p>
                </div>
             </div>

             <table className="w-full mb-16 relative z-10 border-t border-b border-gray-200 dark:border-[#333]">
                <thead className="bg-gray-50 dark:bg-[#202020]">
                   <tr>
                      <th className="text-left py-3 px-4 text-[10px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-300">Description</th>
                      <th className="text-right py-3 px-4 text-[10px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-300">Qty</th>
                      <th className="text-right py-3 px-4 text-[10px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-300">Price</th>
                      <th className="text-right py-3 px-4 text-[10px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-300">Total</th>
                   </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-[#2a2a2a]">
                   {hasItems ? doc.items.map((item: any, idx: number) => (
                     <tr key={idx}>
                        <td className="py-6 px-4">
                           <p className="text-sm font-bold text-[#161616] dark:text-white">{item.name}</p>
                           <p className="text-[10px] font-mono text-gray-500 mt-1">{item.id}</p>
                        </td>
                        <td className="py-6 px-4 text-right font-medium text-[#161616] dark:text-gray-300">{item.qty.toFixed(2)}</td>
                        <td className="py-6 px-4 text-right font-medium text-[#161616] dark:text-gray-300">{formatSwiss(item.price)}</td>
                        <td className="py-6 px-4 text-right font-bold text-[#161616] dark:text-white">{formatSwiss(item.qty * item.price)}</td>
                     </tr>
                   )) : (
                     <tr>
                        <td className="py-6 px-4">
                           <p className="text-sm font-bold text-[#161616] dark:text-white">{doc.title || 'Product Fulfillment'}</p>
                           <p className="text-xs text-gray-500 mt-1">Automated batch processing delivery</p>
                        </td>
                        <td className="py-6 px-4 text-right font-medium text-[#161616] dark:text-gray-300">1.00</td>
                        <td className="py-6 px-4 text-right font-medium text-[#161616] dark:text-gray-300">{formatSwiss(amountNet)}</td>
                        <td className="py-6 px-4 text-right font-bold text-[#161616] dark:text-white">{formatSwiss(amountNet)}</td>
                     </tr>
                   )}
                </tbody>
             </table>

             <div className="flex justify-end relative z-10">
                <div className="w-64 space-y-4">
                   <div className="flex justify-between text-xs font-medium text-gray-500">
                      <span>Net Total</span>
                      <span>{formatSwiss(amountNet)} CHF</span>
                   </div>
                   <div className="flex justify-between text-xs font-medium text-gray-500">
                      <span>VAT 8.1%</span>
                      <span>{formatSwiss(amountGross - amountNet)} CHF</span>
                   </div>
                   <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-[#333] text-[#161616] dark:text-white">
                      <span className="text-sm font-bold uppercase">Grand Total</span>
                      <span className="text-xl font-bold">{formatSwiss(amountGross)}</span>
                   </div>
                </div>
             </div>
          </div>
        </div>

        {/* Sidebar Log */}
        <div className="w-80 border-l border-gray-200 dark:border-[#393939] bg-white dark:bg-[#161616] flex flex-col shrink-0 z-20">
           <div className="h-14 border-b border-gray-200 dark:border-[#393939] px-6 flex items-center bg-gray-50 dark:bg-[#1A1A1A]">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-300">Audit Trail</h3>
           </div>
           <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
              {[
                { label: 'Request Ingested', date: docDate + ', 09:00', icon: Check, active: true },
                { label: 'Logic Validation', date: docDate + ', 09:05', icon: Check, active: true },
                { label: 'Approval Queue', date: docDate + ', 09:10', icon: Check, active: true },
                { label: 'Fulfillment Active', date: docDate + ', 09:11', icon: Check, active: doc.status !== 'Failed' && doc.status !== 'FAILED' && doc.status !== 'ERROR' }
              ].map((step, i) => (
                <div key={i} className={`flex gap-4 group relative ${!step.active ? 'opacity-50 grayscale' : ''}`}>
                   {i !== 3 && <div className="absolute left-3 top-6 bottom-[-32px] w-px bg-gray-200 dark:bg-gray-800"></div>}
                   <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 ${step.active ? 'bg-[#717C26]' : 'bg-gray-300 dark:bg-gray-700'}`}>
                      <step.icon size={12} className="text-white" />
                   </div>
                   <div>
                      <p className="text-xs font-bold text-[#161616] dark:text-gray-200">{step.label}</p>
                      <p className="text-[10px] text-gray-500 mt-1 font-mono">{step.date}</p>
                   </div>
                </div>
              ))}
              
              {(doc.status === 'failed' || doc.status === 'FAILED' || doc.status === 'ERROR') && (
                 <div className="flex gap-4 group relative">
                    <div className="w-6 h-6 rounded-full bg-[#DA1E28] flex items-center justify-center shrink-0 z-10">
                       <span className="text-white font-bold text-[10px]">!</span>
                    </div>
                    <div>
                       <p className="text-xs font-bold text-[#DA1E28]">Failed</p>
                       <p className="text-[10px] text-gray-500 mt-1 font-mono">{docDate}, 09:12</p>
                       <p className="text-[10px] text-red-700 bg-red-50 p-2 mt-2 rounded border border-red-100 leading-snug">
                          Error: Connection timeout at endpoint gateway.
                       </p>
                    </div>
                 </div>
              )}
           </div>
        </div>
      </div>
    </div>
  );
};
